import { FabricEditor, FabricEditorCanvas, FabricEditorProvider } from 'modules/fabric-editor';
import React, { useCallback, useEffect, useState } from 'react';
import { historyPlugin, hotkeyPlugin, selectionPlugin, zoomPlugin } from '../../domain/plugins';
import { useFabricEditor } from '../context';

const PLUGINS = [historyPlugin(), zoomPlugin(), selectionPlugin(), hotkeyPlugin()];

const FabricEditorDemoPage: React.FC = () => {
  const [editor, setEditor] = useState<FabricEditor | null>(null);

  return (
    <FabricEditorProvider editor={editor}>
      <div className={'h-full'} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600 }}>Fabric Editor — Demo</h1>
        <div className={'flex-1'} style={{ border: '1px solid #d1d5db' }}>
          <FabricEditorCanvas resolution={{ width: 1920, height: 1080 }} plugins={PLUGINS} onReady={setEditor} />
        </div>
        {editor ? (
          <>
            <Toolbar />
            <ExternalActions />
          </>
        ) : null}
      </div>
    </FabricEditorProvider>
  );
};

const Toolbar: React.FC = () => {
  const editor = useFabricEditor();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [selectionCount, setSelectionCount] = useState(0);

  useEffect(() => {
    const offHist = editor.on('history:changed', (e) => {
      setCanUndo(e.canUndo);
      setCanRedo(e.canRedo);
    });
    const offZoom = editor.on('zoom:changed', (e) => setZoom(e.zoom));
    const offSel = editor.on('selection:changed', (e) => setSelectionCount(e.active.length));
    return () => {
      offHist();
      offZoom();
      offSel();
    };
  }, [editor]);

  const addText = useCallback(() => {
    editor.objects.addText({ text: 'Hello', left: 200, top: 200, fontSize: 48 });
  }, [editor]);

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <button onClick={addText}>Add text</button>
      <button onClick={() => editor.history?.undo()} disabled={!canUndo}>
        Undo
      </button>
      <button onClick={() => editor.history?.redo()} disabled={!canRedo}>
        Redo
      </button>
      <button onClick={() => editor.zoom?.in()}>Zoom +</button>
      <button onClick={() => editor.zoom?.out()}>Zoom -</button>
      <button onClick={() => editor.zoom?.reset()}>Zoom reset</button>
      <span style={{ marginLeft: 'auto', fontSize: 12, color: '#666' }}>
        zoom: {zoom.toFixed(2)} · selected: {selectionCount}
      </span>
    </div>
  );
};

// Sibling of FabricEditorCanvas — proves useFabricEditor() works outside the canvas subtree.
const ExternalActions: React.FC = () => {
  const editor = useFabricEditor();
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <button onClick={() => editor.objects.addRect({ left: 300, top: 300, width: 200, height: 120, fill: '#4f46e5' })}>
        Add rect (external)
      </button>
    </div>
  );
};

export default FabricEditorDemoPage;
